/**
 * Single mail template: 件名・本文、編集、保存、デフォルトに戻す
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchMailTemplate,
  updateMailTemplate,
  resetMailTemplate,
  type MailTemplateKeyApi,
} from '../../../../services/mailTemplateApi';
import './MailTemplateTabPanel.css';

export interface MailTemplateTabPanelProps {
  templateKey: MailTemplateKeyApi;
  subjectPlaceholderHint: string;
  bodyPlaceholderHint: string;
}

const MailTemplateTabPanel: React.FC<MailTemplateTabPanelProps> = ({
  templateKey,
  subjectPlaceholderHint,
  bodyPlaceholderHint,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [baseline, setBaseline] = useState<{ subject: string; body: string }>({ subject: '', body: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const row = await fetchMailTemplate(templateKey);
      setSubject(row.subject);
      setBody(row.body);
      setBaseline({ subject: row.subject, body: row.body });
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [templateKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('件名と本文を入力してください。');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const row = await updateMailTemplate(templateKey, { subject: subject.trim(), body: body.trim() });
      setSubject(row.subject);
      setBody(row.body);
      setBaseline({ subject: row.subject, body: row.body });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('工場出荷時のデフォルト文言に戻しますか？現在の編集内容は失われます。')) return;
    setSaving(true);
    setError(null);
    try {
      const row = await resetMailTemplate(templateKey);
      setSubject(row.subject);
      setBody(row.body);
      setBaseline({ subject: row.subject, body: row.body });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : '復元に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setSubject(baseline.subject);
    setBody(baseline.body);
    setEditing(false);
  };

  if (loading) {
    return <p className="mail-template-panel__loading">読み込み中…</p>;
  }

  return (
    <div className="mail-template-panel">
      {error && (
        <p className="mail-template-panel__error" role="alert">
          {error}
        </p>
      )}

      <p className="mail-template-panel__hint">
        <strong>件名の注意:</strong> {subjectPlaceholderHint}
      </p>
      <p className="mail-template-panel__hint">
        <strong>本文の注意:</strong> {bodyPlaceholderHint}
      </p>

      <div className="mail-template-panel__row">
        <label className="mail-template-panel__label" htmlFor={`mt-subj-${templateKey}`}>
          件名
        </label>
        <input
          id={`mt-subj-${templateKey}`}
          type="text"
          className="mail-template-panel__input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          readOnly={!editing}
          disabled={saving}
        />
      </div>

      <div className="mail-template-panel__row mail-template-panel__row--body">
        <label className="mail-template-panel__label" htmlFor={`mt-body-${templateKey}`}>
          本文
        </label>
        <textarea
          id={`mt-body-${templateKey}`}
          className="mail-template-panel__textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          readOnly={!editing}
          disabled={saving}
          rows={16}
        />
      </div>

      <div className="mail-template-panel__actions">
        {!editing ? (
          <>
            <button type="button" className="mail-template-panel__btn mail-template-panel__btn--primary" onClick={() => setEditing(true)} disabled={saving}>
              編集
            </button>
            <button type="button" className="mail-template-panel__btn mail-template-panel__btn--ghost" onClick={() => void handleReset()} disabled={saving}>
              デフォルトに戻す
            </button>
          </>
        ) : (
          <>
            <button type="button" className="mail-template-panel__btn mail-template-panel__btn--primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? '保存中…' : '保存'}
            </button>
            <button type="button" className="mail-template-panel__btn" onClick={handleCancelEdit} disabled={saving}>
              キャンセル
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MailTemplateTabPanel;
